import { Component, HostListener, OnInit, OnDestroy, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import {
  Router,
  GuardsCheckEnd,
  NavigationEnd,
  NavigationCancel,
  NavigationError,
  RouterOutlet,
} from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { MessagingService } from './core/services/messaging.service';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter, map } from 'rxjs/operators';
import posthog from 'posthog-js';
import { UserService } from './auth/services/user.service';
import { SpinnerMessageService } from './shared/services/spinner-message.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ConnectionQualityMonitorService } from './shared/services/connection-quality-monitor.service';
import { GlobalLoadingFacadeService } from './core/state/global-loading/global-loading-facade.service';
import { GlobalLoadingSpinnerComponent } from './core/components/global-loading-spinner/global-loading-spinner.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.sass'],
  imports: [GlobalLoadingSpinnerComponent, RouterOutlet],
})
export class AppComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private globalLoading = inject(GlobalLoadingFacadeService);
  private swUpdate = inject(SwUpdate);
  spinnerMsg = inject(SpinnerMessageService);
  private snackBar = inject(MatSnackBar);
  private connectionQualityMonitor = inject(ConnectionQualityMonitorService);
  private doc = inject<Document>(DOCUMENT);

  private destroy$ = new Subject<void>();
  private navigationTimeout: any;

  title = 'mProcesses - Your Business in Your Pocket';

  private touchStartY: number = 0;

  ngOnDestroy() {
    clearTimeout(this.navigationTimeout);
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit() {
    // Tag body with environment class for theming
    try {
      const envName = (environment as any)?.env || (environment as any)?.envName || 'stage';
      const cls = `env-${envName}`;
      this.doc?.body?.classList?.add(cls);
      console.log(`[AppComponent] Applied environment class: ${cls}`);
    } catch {}

    // Initialize connection quality monitoring
    console.log('🌐 [AppComponent] Initializing connection quality monitoring');
    // The service automatically starts monitoring when injected

    // ✅ App-level router spinner for lazy module loading
    // Provides immediate feedback during module download and resolver execution
    // Coordinates with module-level spinners via source tracking
    this.setupRouterSpinner();

    if (this.swUpdate.isEnabled) {
      // Check for updates every 5 minutes (more frequent than hourly)
      setInterval(
        () => {
          this.swUpdate.checkForUpdate();
        },
        5 * 60 * 1000
      );

      // Check for updates immediately on app startup
      this.swUpdate.checkForUpdate().then(() => {
        console.log('[PWA] Initial update check completed');
      });

      // Check for updates when app becomes visible (user switches back to tab)
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden && this.swUpdate.isEnabled) {
          console.log('[PWA] App became visible, checking for updates...');
          this.swUpdate.checkForUpdate();
        }
      });

      // Handle version updates
      this.swUpdate.versionUpdates
        .pipe(
          filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'),
          takeUntil(this.destroy$)
        )
        .subscribe((event) => {
          console.log(`[PWA] New version available: ${event.latestVersion.hash}`);

          // Show update notification with auto-dismiss after 30 seconds
          const snackBarRef = this.snackBar.open('A new version is available. Updating...', 'Update Now', {
            duration: 30000,
          });

          // Auto-reload after 5 seconds if user doesn't click
          const autoReloadTimer = setTimeout(() => {
            console.log('[PWA] Auto-reloading to apply new version');
            window.location.reload();
          }, 5000);

          // Handle manual update action
          snackBarRef
            .onAction()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
              clearTimeout(autoReloadTimer);
              console.log('[PWA] User triggered reload');
              window.location.reload();
            });

          // Clear timer if snackbar is dismissed
          snackBarRef
            .afterDismissed()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
              clearTimeout(autoReloadTimer);
            });
        });

      // Handle unrecoverable errors
      this.swUpdate.unrecoverable.pipe(takeUntil(this.destroy$)).subscribe((event) => {
        console.error('[PWA] Unrecoverable error:', event.reason);
        this.snackBar
          .open('An error occurred that requires a reload', 'Reload', { duration: 0 })
          .onAction()
          .subscribe(() => {
            window.location.reload();
          });
      });
    }
  }

  /**
   * ✅ App-level router spinner management
   * Shows spinner AFTER guards pass (to avoid blocking canDeactivate dialogs)
   * Covers module loading and resolver execution
   * Hides on navigation complete, but only if no module-level spinner has taken over
   */
  private setupRouterSpinner(): void {
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      // ✅ Show spinner AFTER guards pass (GuardsCheckEnd)
      // This prevents blocking canDeactivate/canActivate dialogs
      if (event instanceof GuardsCheckEnd) {
        // Only show if guards passed (user confirmed or no guards blocked)
        if (event.shouldActivate) {
          // Clear any pending hide timeout
          clearTimeout(this.navigationTimeout);

          // Show spinner with context-aware message
          const message = this.getMessageForRoute(event.url);
          this.globalLoading.show(message, 'app-router');

          console.log(`🔄 [AppRouter] Guards passed, navigation to: ${event.url}`);
        } else {
          // Guards blocked navigation (user clicked "No" in canDeactivate)
          console.log(`🚫 [AppRouter] Guards blocked navigation to: ${event.url}`);
        }
      }

      if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
        console.log(`✅ [AppRouter] Navigation ${event instanceof NavigationEnd ? 'completed' : 'cancelled/error'}`);

        // Small delay to allow module-level spinners to take over
        this.navigationTimeout = setTimeout(() => {
          const currentState = this.globalLoading.getState();

          // Only hide if:
          // 1. We're the ones showing it (source is 'app-router'), OR
          // 2. Source is 'unknown' (safety fallback)
          // If module-level spinner (e.g., 'salesrep-module') took over, don't hide
          if (currentState.source === 'app-router' || currentState.source === 'unknown') {
            console.log(`🔽 [AppRouter] Hiding spinner (source was: ${currentState.source})`);
            this.globalLoading.hide();
          } else {
            console.log(`⏭️  [AppRouter] Module-level spinner active (source: ${currentState.source}), not hiding`);
          }
        }, 150); // Small delay for smooth handoff
      }

      // Handle navigation errors
      if (event instanceof NavigationError) {
        console.error('❌ [AppRouter] Navigation error:', event.error);
        // Force hide on error after showing error message
        this.globalLoading.updateMessage('Greška pri učitavanju.');
        setTimeout(() => {
          this.globalLoading.hide();
        }, 2000);
      }
    });
  }

  /**
   * Get user-friendly loading message based on destination route
   */
  private getMessageForRoute(url: string): string {
    // Salesrep module - specific routes
    if (url.includes('/salesrep')) {
      // Opening specific order (e.g., /salesrep/customers/:id/customer-details/:orderId)
      if (url.match(/\/customer-details\/[^/]+$/) && url.split('/').length > 6) {
        return 'Učitavanje porudžbine...';
      }
      // Opening customer details
      if (url.includes('/customer-details')) {
        return 'Učitavanje podataka kupca...';
      }
      // Adding new customer
      if (url.includes('/add-new-customer')) {
        return 'Priprema forme...';
      }
      // Module entry or customers list
      return 'Učitavanje komercijale...';
    }

    // Other modules
    if (url.includes('/warehouse-orders')) return 'Učitavanje distribucije...';
    if (url.includes('/procurement-old')) return 'Učitavanje nabavke...';
    if (url.includes('/procurement')) return 'Učitavanje nabavke...';
    if (url.includes('/expenses')) return 'Učitavanje troškova...';
    if (url.includes('/timesheets')) return 'Učitavanje evidencije...';
    if (url.includes('/users-management')) return 'Učitavanje podešavanja...';
    if (url.includes('/profile')) return 'Učitavanje profila...';
    if (url.includes('/account')) return 'Učitavanje naloga...';

    return 'Učitavanje...';
  }

  onWindowTouchStart = (event: TouchEvent): void => {
    this.touchStartY = event.touches[0].screenY;
  };

  onWindowTouchMove = (event: TouchEvent): void => {
    const touch = event.touches[0];
    const isSwipeDown = touch.screenY > this.touchStartY;

    // Check if the swipe is at the top of the page
    if (isSwipeDown && window.scrollY === 0) {
      event.preventDefault();
    }
  };

  //block pull-to-refresh
  // @HostListener('window:touchmove', ['$event'])
  // onWindowTouchMove(event: TouchEvent): void {

  //   // Check the first touch point
  //   if (event.touches.length === 1) {
  //     const touch = event.touches[0];

  //     // Prevent default behavior if the touch is a swipe down
  //     if (touch.screenY > 0) {
  //       event.preventDefault();
  //     }
  //   }
  // }
}
